<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="Sync Questions to Custom Fields"
    :style="{ width: '40rem' }"
    :closable="!isCreating"
    @hide="onHide"
  >
    <div class="space-y-4">
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Create {{ fieldLabel.toLowerCase() }}s for each question and map them automatically.
        Answers will be synced to {{ integratedPartnerName }} when visitors respond.
      </p>

      <!-- Prefix field -->
      <FormGroup label="Field Name Prefix">
        <InputText
          v-model="currentPrefix"
          class="w-48"
          placeholder="q_"
          :disabled="isCreating"
          @blur="handlePrefixChange"
        />
        <small class="text-surface-500 dark:text-surface-400">
          Applied to the beginning of each field name below
        </small>
      </FormGroup>

      <!-- Questions table -->
      <div class="rounded-lg border border-gray-200 dark:border-gray-700">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              <th class="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">
                Question
              </th>
              <th class="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">
                {{ fieldLabel }} Name
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in fieldRows"
              :key="row.id"
              class="border-b border-gray-100 transition-opacity duration-300 last:border-b-0 dark:border-gray-700/50"
              :class="{ 'opacity-50': isCreating && !completedRows.has(row.id) }"
            >
              <td class="px-3 py-2">
                <div class="flex items-center justify-between gap-2">
                  <div>
                    <div
                      v-if="row.dimensionName"
                      class="text-xs text-gray-400 dark:text-gray-500"
                    >
                      {{ row.dimensionName }}
                    </div>
                    <div class="text-gray-900 dark:text-gray-200">
                      {{ row.label }}
                    </div>
                  </div>
                  <i
                    v-if="row.matchedFieldId"
                    v-tooltip.top="'Already exists — will use existing field'"
                    class="pi pi-check-circle shrink-0 text-green-500"
                  />
                </div>
              </td>
              <td class="px-3 py-2">
                <InputText
                  v-model="row.fieldName"
                  class="w-full"
                  :class="{ 'p-invalid': row.error }"
                  size="small"
                  :disabled="isCreating"
                  @input="validateRow(index)"
                />
                <small
                  v-if="row.error"
                  class="text-red-600 dark:text-red-400"
                >
                  {{ row.error }}
                </small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        outlined
        :disabled="isCreating"
        @click="visible = false"
      />
      <Button
        :label="bulkActionLabel"
        :disabled="!isValid || isCreating"
        :loading="isCreating"
        @click="handleBulkCreate"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import partnerEndpoints from '@/modules/integration/data/partnerEndpoints'
import { useIntegrationDataStore } from '@/modules/integration/stores/integrationDataStore'
import { generateCombinationsForSegmentGroup } from '@/modules/segment/services/segmentUtilities'
import { repairSegmentGroup } from '@/modules/segment/services/segmentActions'

interface UnsyncedQuestion {
  id: string
  label: string
  dimensionId?: string | null
  dimensionName?: string
  isInput: boolean
}

interface Props {
  modelValue: boolean
  questions: UnsyncedQuestion[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'synced': []
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const route = useRoute()
const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)
const integrationDataStore = useIntegrationDataStore()
const configStore = useConfigStore()
const { currentTeamPid } = storeToRefs(configStore)
const segmentGroupStore = useSegmentGroupStore()
const questionStore = useQuestionStore()
const notificationStore = useNotificationStore()

const isCreating = ref(false)
const previousPrefix = ref('q_')
const currentPrefix = ref('q_')
const customFieldsData = ref<any[]>([])
const completedRows = ref(new Set<string>())

const isUnstructured = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  return customField && typeof customField === 'object' && 'unstructured' in customField
    ? customField.unstructured
    : false
})

const fieldLabel = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  if (customField && typeof customField === 'object' && 'label' in customField) {
    return customField.label
  }
  return 'Custom Field'
})

const integratedPartnerName = computed(() => {
  return integratedPartner.value?.label || integratedPartner.value?.name || 'your ESP'
})

interface FieldRow {
  id: string
  label: string
  dimensionId?: string | null
  dimensionName?: string
  isInput: boolean
  fieldName: string
  error: string
  matchedFieldId: string | null
}

const fieldRows = ref<FieldRow[]>([])

// Generate a prefixed snake_case field name from a human-readable name
const toFieldName = (name: string, prefix: string): string => {
  return `${prefix}${toSnakeCase(name)}`
}

// Initialize rows when modal opens
watch(visible, async (isVisible) => {
  if (isVisible) {
    currentPrefix.value = 'q_'
    previousPrefix.value = 'q_'
    completedRows.value = new Set()

    // Load existing custom fields for matching
    if (!isUnstructured.value) {
      try {
        customFieldsData.value = await integrationDataStore.getCustomFields()
      } catch (error) {
        console.error('Error loading custom fields:', error)
        customFieldsData.value = []
      }
    }

    fieldRows.value = props.questions.map((q) => ({
      id: q.id,
      label: q.label,
      dimensionId: q.dimensionId,
      isInput: q.isInput,
      dimensionName: q.dimensionName,
      fieldName: toFieldName(q.dimensionName || q.label, 'q_'),
      error: '',
      matchedFieldId: null
    }))

    // Run initial validation to detect existing fields
    fieldRows.value.forEach((_, i) => validateRow(i))
  }
})

// Handle prefix change: swap old prefix for new in each row
const handlePrefixChange = () => {
  const oldPrefix = previousPrefix.value
  const newPrefix = currentPrefix.value

  if (oldPrefix === newPrefix) return

  fieldRows.value.forEach((row) => {
    if (row.fieldName.startsWith(oldPrefix)) {
      row.fieldName = newPrefix + row.fieldName.slice(oldPrefix.length)
    }
  })

  previousPrefix.value = newPrefix

  // Re-validate all rows
  fieldRows.value.forEach((_, i) => validateRow(i))
}

// Validate a single row
const validateRow = (index: number) => {
  const row = fieldRows.value[index]
  row.error = ''
  row.matchedFieldId = null

  if (!row.fieldName) {
    row.error = 'Field name is required'
    return
  }

  if (!/^[a-zA-Z0-9_]+$/.test(row.fieldName)) {
    row.error = 'Only letters, numbers, and underscores'
    return
  }

  // Check for duplicates within the table
  const duplicate = fieldRows.value.find(
    (r, i) => i !== index && r.fieldName.toLowerCase() === row.fieldName.toLowerCase()
  )
  if (duplicate) {
    row.error = 'Duplicate field name'
    return
  }

  // Check if field already exists in ESP (structured only)
  if (!isUnstructured.value) {
    const existing = customFieldsData.value.find(
      (field: any) => String(field.key || '').toLowerCase() === row.fieldName.toLowerCase() ||
                      String(field.name || '').toLowerCase() === row.fieldName.toLowerCase() ||
                      String(field.label || '').toLowerCase() === row.fieldName.toLowerCase()
    )
    if (existing) {
      row.matchedFieldId = String(existing.id)
    }
  }
}

const isValid = computed(() => {
  return fieldRows.value.length > 0 &&
    fieldRows.value.every((row) => row.fieldName && !row.error)
})

const bulkActionLabel = computed(() => {
  if (isCreating.value) {
    const done = completedRows.value.size
    const total = fieldRows.value.length
    return `Working... ${done}/${total}`
  }
  const total = fieldRows.value.length
  const existingCount = fieldRows.value.filter((r) => r.matchedFieldId).length
  const newCount = total - existingCount
  const fieldWord = total === 1 ? fieldLabel.value : fieldLabel.value + 's'
  if (newCount === 0) return `Map ${total} ${fieldWord}`
  return `Create & Map ${total} ${fieldWord}`
})

// Bulk create fields, map them, and generate AI mappings
const handleBulkCreate = async () => {
  // Final validation pass
  fieldRows.value.forEach((_, i) => validateRow(i))
  if (!isValid.value) return

  isCreating.value = true
  completedRows.value = new Set()
  let successCount = 0
  let errorCount = 0
  const teamPid = (route.params.teamPid as string) || String(currentTeamPid.value)

  try {
    for (const row of fieldRows.value) {
      try {
        let fieldId: string

        if (isUnstructured.value) {
          // Unstructured: just use the field name directly as the mapping value
          fieldId = row.fieldName
        } else if (row.matchedFieldId) {
          // Field already exists in ESP — use its ID directly
          fieldId = row.matchedFieldId
        } else {
          // Structured: create the field via API
          const response = await partnerEndpoints.createCustomField.mutationFn(
            currentTeamPid.value as number,
            row.fieldName
          )

          if (!response.data?.id) {
            throw new Error('No field ID returned')
          }

          fieldId = String(response.data.id)
        }

        // Map the field to the dimension or input question
        if (row.isInput) {
          // Input question: set syncToField directly
          const question = questionStore.getById(row.id)
          if (question) {
            question.syncToField = fieldId
          }
        } else if (row.dimensionId) {
          // Dimension-linked question: set on the dimension
          const dimension = segmentGroupStore.getById(row.dimensionId)
          if (dimension) {
            dimension.syncToCustomField = fieldId
            dimension.syncTo = integratedPartner.value?.name || null

            // Create signal structures so autoSyncLogicDefinitionForSegment can write to them
            repairSegmentGroup(dimension, { partner: integratedPartner.value! })

            // Generate AI sync value mappings for each segment
            try {
              await generateCombinationsForSegmentGroup(dimension, teamPid)
            } catch (mappingError) {
              console.error(`AI mapping generation failed for "${row.label}":`, mappingError)
              // Non-fatal: field is mapped, just missing auto-generated values
            }
          }
        }

        successCount++
      } catch (error) {
        console.error(`Failed to create/map field for "${row.label}":`, error)
        errorCount++
      }

      // Mark row as completed regardless of success/failure
      completedRows.value = new Set([...completedRows.value, row.id])
    }

    // Fix any questions with invalid syncTo values (e.g., partner name like "bento").
    // question.syncTo is dead code (engine only reads syncToField), but the schema
    // validates it as oneOf: [null, 'customField', 'variable']. Null it out.
    for (const q of questionStore.questions) {
      if (q.syncTo && q.syncTo !== 'customField' && q.syncTo !== 'variable') {
        q.syncTo = null as any
      }
    }

    // Clear custom fields cache so future fetches get the new fields
    integrationDataStore.clearCache('partner', 'custom_fields')

    if (errorCount === 0) {
      notificationStore.addNotification(
        `Successfully created and mapped ${successCount} ${successCount === 1 ? fieldLabel.value.toLowerCase() : fieldLabel.value.toLowerCase() + 's'}`
      )
    } else {
      notificationStore.addNotification(
        `Created ${successCount} fields, ${errorCount} failed`
      )
    }

    emit('synced')
    visible.value = false
  } catch (error) {
    console.error('Bulk field creation error:', error)
    notificationStore.addError('Failed to create custom fields. Please try again.')
  } finally {
    isCreating.value = false
  }
}

const onHide = () => {
  fieldRows.value = []
  customFieldsData.value = []
  completedRows.value = new Set()
  currentPrefix.value = 'q_'
  previousPrefix.value = 'q_'
}
</script>
